import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  MessageSquare, Mail, Gift, MonitorCheck, Plus, Search, Trash2, Pencil,
  CheckCircle2, Circle, Loader2, ClipboardCheck, Users, Send, ChevronDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_DEFS = [
  { key: 'text_sent', label: 'Post-sale text sent', desc: 'From your personal cell', icon: MessageSquare, color: 'text-sky-600', bg: 'bg-sky-50' },
  { key: 'email_sent', label: 'Post-sale email sent', desc: 'Branded recap sent from ATLAS', icon: Mail, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { key: 'gift_sent', label: 'Thank-you card / gift sent', desc: 'Card or gift mailed to client', icon: Gift, color: 'text-rose-600', bg: 'bg-rose-50' },
  { key: 'portal_setup', label: 'Client portal set up', desc: 'Carrier portal access created', icon: MonitorCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
];

const EMPTY_FORM = {
  client_name: '', client_email: '', client_phone: '', carrier: '', product_type: '',
  coverage_amount: '', premium: '', premium_mode: 'monthly', policy_number: '', draft_date: '', beneficiaries: '', client_why: '', notes: '',
};

const doneCount = (c) => STEP_DEFS.filter((s) => c.steps?.[s.key]?.done).length;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const inputCls = 'w-full text-sm px-3 py-2 rounded-lg border border-[#E9EDF7] bg-white text-[#2B3674] placeholder:text-[#A3AED0] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40';

export default function PostSaleProcess() {
  const { user, getAuthHeader } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('mine'); // 'mine' | 'team' (admin)

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [emailClient, setEmailClient] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [teamData, setTeamData] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/post-sale/clients`, getAuthHeader());
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load post-sale clients');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    if (view === 'team' && isAdmin) {
      axios.get(`${API}/post-sale/team-overview`, getAuthHeader())
        .then((res) => setTeamData(Array.isArray(res.data) ? res.data : []))
        .catch(() => toast.error('Failed to load team overview'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client_name: c.client_name || '', client_email: c.client_email || '', client_phone: c.client_phone || '',
      carrier: c.carrier || '', product_type: c.product_type || '',
      coverage_amount: c.coverage_amount || '', premium: c.premium || '', premium_mode: c.premium_mode || 'monthly',
      policy_number: c.policy_number || '', client_why: c.client_why || '', notes: c.notes || '',
    });
    setFormOpen(true);
  };

  const saveClient = async () => {
    if (!form.client_name.trim()) { toast.error('Client name is required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      coverage_amount: parseFloat(form.coverage_amount) || 0,
      premium: parseFloat(form.premium) || 0,
    };
    try {
      if (editing) {
        const res = await axios.put(`${API}/post-sale/clients/${editing.id}`, payload, getAuthHeader());
        setClients((prev) => prev.map((c) => (c.id === editing.id ? res.data : c)));
        toast.success('Client updated');
      } else {
        const res = await axios.post(`${API}/post-sale/clients`, payload, getAuthHeader());
        setClients((prev) => [res.data, ...prev]);
        toast.success('Sold client added — time to run the post-sale play!');
      }
      setFormOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Remove ${c.client_name} from your post-sale list?`)) return;
    try {
      await axios.delete(`${API}/post-sale/clients/${c.id}`, getAuthHeader());
      setClients((prev) => prev.filter((x) => x.id !== c.id));
      toast.success('Client removed');
    } catch (e) {
      toast.error('Failed to remove client');
    }
  };

  const toggleStep = async (c, stepKey) => {
    const newDone = !c.steps?.[stepKey]?.done;
    try {
      const res = await axios.put(`${API}/post-sale/clients/${c.id}/steps/${stepKey}`, { done: newDone }, getAuthHeader());
      setClients((prev) => prev.map((x) => (x.id === c.id ? res.data : x)));
      if (newDone && doneCount(res.data) === 4) toast.success(`${c.client_name} — post-sale process complete! 🎉`);
    } catch (e) {
      toast.error('Failed to update step');
    }
  };

  const openEmailPreview = async (c) => {
    setEmailClient(c);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/post-sale/clients/${c.id}/email-preview`, getAuthHeader());
      setPreview(res.data);
    } catch (e) {
      toast.error('Failed to build email preview');
      setEmailClient(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!emailClient) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/post-sale/clients/${emailClient.id}/send-email`, {}, getAuthHeader());
      setClients((prev) => prev.map((x) => (x.id === emailClient.id ? res.data.client : x)));
      toast.success(res.data.message);
      setEmailClient(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed to send');
    } finally {
      setSending(false);
    }
  };

  const filtered = clients.filter((c) => {
    if (filter === 'pending' && doneCount(c) === 4) return false;
    if (filter === 'complete' && doneCount(c) < 4) return false;
    if (search && !(c.client_name || '').toLowerCase().includes(search.toLowerCase())
      && !(c.policy_number || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pendingCount = clients.filter((c) => doneCount(c) < 4).length;

  return (
    <div className="space-y-5" data-testid="post-sale-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2B3674] flex items-center gap-2.5">
            <ClipboardCheck className="h-7 w-7 text-[#2447D6]" />
            Post-Sale Process
          </h1>
          <p className="text-sm text-[#A3AED0] mt-1">
            Run the same 4-step play after every sale — text, email, gift, portal.
            {pendingCount > 0 && <span className="text-amber-600 font-medium"> {pendingCount} client{pendingCount === 1 ? '' : 's'} need attention.</span>}
          </p>
        </div>
        <Button onClick={openNew} data-testid="post-sale-add-client-btn" className="bg-[#2447D6] hover:bg-[#1E3FBF] text-white rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> New Sold Client
        </Button>
      </div>

      {/* Admin view switch */}
      {isAdmin && (
        <div className="flex items-center gap-1">
          {[{ k: 'mine', label: 'My Clients', icon: ClipboardCheck }, { k: 'team', label: 'Team Overview', icon: Users }].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setView(t.k)}
              data-testid={`post-sale-view-${t.k}`}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                view === t.k ? 'bg-[#2B3674] text-white' : 'bg-white border border-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-50'
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
      )}

      {view === 'team' && isAdmin ? (
        <TeamOverview data={teamData} />
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
            <div className="flex items-center gap-1">
              {[{ k: 'all', label: `All (${clients.length})` }, { k: 'pending', label: 'Needs Attention' }, { k: 'complete', label: 'Complete' }].map((f) => (
                <button
                  key={f.k}
                  type="button"
                  onClick={() => setFilter(f.k)}
                  data-testid={`post-sale-filter-${f.k}`}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filter === f.k ? 'bg-[#2447D6] text-white' : 'bg-white border border-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A3AED0]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client or policy #..."
                data-testid="post-sale-search"
                className={`${inputCls} pl-9`}
              />
            </div>
          </div>

          {/* Client cards */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" /></div>
          ) : filtered.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-14 text-center">
                <ClipboardCheck className="h-10 w-10 text-[#A3AED0] mx-auto mb-3" />
                <p className="text-[#2B3674] font-semibold">{clients.length === 0 ? 'No sold clients yet' : 'No clients match your filter'}</p>
                {clients.length === 0 && (
                  <p className="text-sm text-[#A3AED0] mt-1">Close a deal, then hit "New Sold Client" to run your post-sale play.</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map((c, i) => (
                <ClientCard
                  key={c.id}
                  client={c}
                  index={i}
                  onToggle={toggleStep}
                  onEdit={openEdit}
                  onDelete={deleteClient}
                  onEmail={openEmailPreview}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="post-sale-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Sold Client' : 'New Sold Client'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Client Name *">
              <input className={inputCls} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="John Smith" data-testid="post-sale-form-name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client Email">
                <input className={inputCls} type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="john@email.com" data-testid="post-sale-form-email" />
              </Field>
              <Field label="Client Phone">
                <input className={inputCls} value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="(555) 123-4567" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Carrier">
                <input className={inputCls} value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="Americo" data-testid="post-sale-form-carrier" />
              </Field>
              <Field label="Product">
                <input className={inputCls} value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} placeholder="IUL / Term / Whole Life" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Coverage $">
                <input className={inputCls} type="number" min="0" value={form.coverage_amount} onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })} placeholder="250000" />
              </Field>
              <Field label="Premium $">
                <input className={inputCls} type="number" min="0" step="0.01" value={form.premium} onChange={(e) => setForm({ ...form, premium: e.target.value })} placeholder="150" />
              </Field>
              <Field label="Mode">
                <select className={inputCls} value={form.premium_mode} onChange={(e) => setForm({ ...form, premium_mode: e.target.value })}>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Policy #">
                <input className={inputCls} value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} placeholder="POL-123456" data-testid="post-sale-form-policy" />
              </Field>
              <Field label="Expected Draft Date">
                <input className={inputCls} type="date" value={form.draft_date} onChange={(e) => setForm({ ...form, draft_date: e.target.value })} data-testid="post-sale-form-draft-date" />
              </Field>
            </div>
            <Field label="Beneficiaries">
              <input className={inputCls} value={form.beneficiaries} onChange={(e) => setForm({ ...form, beneficiaries: e.target.value })} placeholder="Jane Smith, John Smith Jr." data-testid="post-sale-form-beneficiaries" />
            </Field>
            <Field label={'Client\'s "Why"'}>
              <textarea className={`${inputCls} min-h-[70px]`} value={form.client_why} onChange={(e) => setForm({ ...form, client_why: e.target.value })} placeholder='"I want my kids taken care of no matter what."' />
            </Field>
            <Field label="Notes (private)">
              <textarea className={`${inputCls} min-h-[50px]`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Gift idea, follow-up reminders..." />
            </Field>
            <Button onClick={saveClient} disabled={saving} data-testid="post-sale-form-save" className="w-full bg-[#2447D6] hover:bg-[#1E3FBF] text-white rounded-xl">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save Changes' : 'Add Client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email preview / send dialog */}
      <Dialog open={!!emailClient} onOpenChange={(open) => { if (!open) setEmailClient(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="post-sale-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" /> Post-Sale Email — {emailClient?.client_name}
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" /></div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="text-xs text-[#2B3674] bg-[#F4F7FE] border border-[#E9EDF7] rounded-lg px-3 py-2 space-y-0.5">
                <div><span className="text-[#A3AED0]">To:</span> <span className="font-semibold" data-testid="post-sale-email-to">{preview.to || '— no client email on file —'}</span></div>
                <div><span className="text-[#A3AED0]">Subject:</span> <span className="font-semibold">{preview.subject}</span></div>
                <div><span className="text-[#A3AED0]">Replies go to:</span> <span className="font-semibold">{preview.reply_to}</span></div>
              </div>
              <div className="border border-[#E9EDF7] rounded-xl overflow-hidden bg-[#EAF3FB]">
                <iframe title="Email preview" srcDoc={preview.html} className="w-full h-[55vh] bg-white" sandbox="" data-testid="post-sale-email-preview-frame" />
              </div>
              {emailClient?.email_meta?.last_sent_at && (
                <p className="text-xs text-amber-600">Already sent {fmtDate(emailClient.email_meta.last_sent_at)} — sending again delivers a duplicate to the client.</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setEmailClient(null)}>Cancel</Button>
                <Button
                  onClick={sendEmail}
                  disabled={sending || !preview.to}
                  data-testid="post-sale-email-send-btn"
                  className="flex-1 bg-[#2447D6] hover:bg-[#1E3FBF] text-white rounded-xl"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-1.5" />{emailClient?.email_meta?.last_sent_at ? 'Send Again' : 'Send Email'}</>)}
                </Button>
              </div>
              {!preview.to && <p className="text-xs text-red-500 text-center">Add the client's email address (edit client) before sending.</p>}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#2B3674] mb-1">{label}</label>
      {children}
    </div>
  );
}

function ClientCard({ client: c, index, onToggle, onEdit, onDelete, onEmail }) {
  const [expanded, setExpanded] = useState(false);
  const done = doneCount(c);
  const complete = done === 4;
  const chips = [c.carrier, c.product_type, c.coverage_amount ? `$${Number(c.coverage_amount).toLocaleString()}` : null,
    c.premium ? `$${Number(c.premium).toLocaleString()}${c.premium_mode === 'annual' ? '/yr' : '/mo'}` : null,
    c.policy_number ? `#${c.policy_number}` : null].filter(Boolean);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.3) }}>
      <Card className={`bg-white ${complete ? 'border-emerald-200' : 'border-[#E9EDF7]'}`} data-testid={`post-sale-client-card-${c.id}`}>
        <CardContent className="p-0">
          {/* Collapsed header row — always visible */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`post-sale-toggle-expand-${c.id}`}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F8FAFF] transition-colors rounded-xl"
          >
            <span className={`flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full text-[11px] font-bold ${
              complete ? 'bg-emerald-100 text-emerald-700' : 'bg-[#F4F7FE] text-[#2447D6]'
            }`}>
              {complete ? <CheckCircle2 className="h-4 w-4" /> : `${done}/4`}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-bold text-sm text-[#2B3674] truncate">{c.client_name}</span>
              <span className="block text-[11px] text-[#A3AED0] truncate">
                {[c.carrier, c.premium ? `$${Number(c.premium).toLocaleString()}${c.premium_mode === 'annual' ? '/yr' : '/mo'}` : null].filter(Boolean).join(' · ') || 'No policy details'}
              </span>
            </span>
            <span className="hidden sm:flex items-center gap-1 flex-shrink-0">
              {STEP_DEFS.map((s) => {
                const stDone = c.steps?.[s.key]?.done;
                return (
                  <span key={s.key} title={s.label} className={`p-1 rounded-md ${stDone ? s.bg : 'bg-[#F4F7FE]'}`}>
                    <s.icon className={`h-3 w-3 ${stDone ? s.color : 'text-[#C9D2E8]'}`} />
                  </span>
                );
              })}
            </span>
            <ChevronDown className={`h-4 w-4 text-[#A3AED0] flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Expanded detail */}
          {expanded && (
          <div className="px-4 pb-4 border-t border-[#F4F7FE]">
          {chips.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3">
              {chips.map((chip, i) => (
                <span key={`${chip}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F7FE] text-[#2B3674]/70 border border-[#E9EDF7]">{chip}</span>
              ))}
            </div>
          )}

          {/* Progress */}
          <div className="flex items-center gap-2 mt-3">
            <div className="flex-1 h-1.5 rounded-full bg-[#E9EDF7] overflow-hidden">
              <div className={`h-full rounded-full transition-all ${complete ? 'bg-emerald-500' : 'bg-[#2447D6]'}`} style={{ width: `${(done / 4) * 100}%` }} />
            </div>
            <span className="text-[11px] font-bold text-[#2B3674]">{done}/4</span>
            <div className="flex items-center gap-1 ml-1">
              <button type="button" onClick={() => onEdit(c)} data-testid={`post-sale-edit-${c.id}`} className="p-1.5 rounded-lg text-[#A3AED0] hover:text-[#2447D6] hover:bg-blue-50 transition-colors">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => onDelete(c)} data-testid={`post-sale-delete-${c.id}`} className="p-1.5 rounded-lg text-[#A3AED0] hover:text-red-500 hover:bg-red-50 transition-colors">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Steps */}
          <div className="mt-3 space-y-1.5">
            {STEP_DEFS.map((s) => {
              const st = c.steps?.[s.key] || {};
              const isEmail = s.key === 'email_sent';
              return (
                <div
                  key={s.key}
                  className={`flex items-center gap-2.5 p-2 rounded-lg border transition-colors ${st.done ? 'bg-emerald-50/60 border-emerald-100' : 'bg-[#F8FAFF] border-[#E9EDF7]'}`}
                >
                  <button type="button" onClick={() => onToggle(c, s.key)} data-testid={`post-sale-step-${s.key}-${c.id}`} className="flex-shrink-0">
                    {st.done
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      : <Circle className="h-5 w-5 text-[#A3AED0] hover:text-[#2447D6] transition-colors" />}
                  </button>
                  <div className={`p-1.5 rounded-md ${s.bg} flex-shrink-0`}>
                    <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold ${st.done ? 'text-emerald-700' : 'text-[#2B3674]'}`}>{s.label}</div>
                    <div className="text-[10px] text-[#A3AED0] truncate">
                      {st.done ? `Done ${fmtDate(st.at)}` : s.desc}
                    </div>
                  </div>
                  {isEmail && (
                    <button
                      type="button"
                      onClick={() => onEmail(c)}
                      data-testid={`post-sale-open-email-${c.id}`}
                      className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        st.done ? 'bg-white border border-[#E9EDF7] text-[#2B3674]/70 hover:bg-slate-50' : 'bg-[#2447D6] text-white hover:bg-[#1E3FBF]'
                      }`}
                    >
                      <Send className="h-3 w-3" /> {st.done ? 'Resend' : 'Preview & Send'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function TeamOverview({ data }) {
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" /></div>;
  if (data.length === 0) {
    return (
      <Card className="bg-white"><CardContent className="py-14 text-center text-[#A3AED0] text-sm">No post-sale clients tracked yet by any agent.</CardContent></Card>
    );
  }
  return (
    <Card className="bg-white" data-testid="post-sale-team-overview">
      <CardContent className="p-4 sm:p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[#A3AED0] border-b border-[#E9EDF7]">
                <th className="py-2 pr-3">Agent</th>
                <th className="py-2 px-2 text-center">Clients</th>
                {STEP_DEFS.map((s) => (
                  <th key={s.key} className="py-2 px-2 text-center"><s.icon className={`h-4 w-4 mx-auto ${s.color}`} /></th>
                ))}
                <th className="py-2 px-2 text-center">Fully Done</th>
                <th className="py-2 pl-2 text-right">Completion</th>
              </tr>
            </thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.agent_id} className="border-b border-[#F4F7FE] last:border-0">
                  <td className="py-2.5 pr-3 font-semibold text-[#2B3674]">{a.agent_name}</td>
                  <td className="py-2.5 px-2 text-center text-[#2B3674]">{a.total_clients}</td>
                  {STEP_DEFS.map((s) => (
                    <td key={s.key} className="py-2.5 px-2 text-center text-[#2B3674]/80">{a.steps_done?.[s.key] ?? 0}</td>
                  ))}
                  <td className="py-2.5 px-2 text-center text-[#2B3674]">{a.complete_clients}</td>
                  <td className="py-2.5 pl-2 text-right">
                    <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${
                      a.completion_pct >= 75 ? 'bg-emerald-100 text-emerald-700' : a.completion_pct >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {a.completion_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
